/** Shape mínima de sesión Auth (evita depender de @supabase/supabase-js en `shared/`). */
export type PasswordRecoverySessionLike = {
  access_token?: string | null;
  // `User` de supabase-js no tipa `amr`; lo leemos en runtime.
  user?: unknown;
};

type AmrEntry = { method?: string } | string;

/**
 * ¿La sesión actual es de recuperación de contraseña (no un login normal)?
 * Compartido negocio (PWA) + portal reset-password.
 */
export function sessionIsPasswordRecovery(session: PasswordRecoverySessionLike | null | undefined): boolean {
  if (!session?.access_token) return false;

  const userAmr =
    session.user && typeof session.user === 'object'
      ? (session.user as { amr?: unknown }).amr
      : undefined;
  const fromUser = readAmrMethods(userAmr);
  if (fromUser.includes('recovery')) return true;

  const fromJwt = readAmrMethods(decodeJwtPayload(session.access_token)?.amr);
  return fromJwt.includes('recovery');
}

export function readAmrMethods(amr: unknown): string[] {
  if (!Array.isArray(amr)) return [];
  const out: string[] = [];
  for (const entry of amr as AmrEntry[]) {
    if (typeof entry === 'string' && entry.trim()) {
      out.push(entry.trim().toLowerCase());
      continue;
    }
    if (entry && typeof entry === 'object' && typeof entry.method === 'string' && entry.method.trim()) {
      out.push(entry.method.trim().toLowerCase());
    }
  }
  return out;
}

export function decodeJwtPayload(accessToken: string): { amr?: unknown } | null {
  try {
    const parts = accessToken.split('.');
    if (parts.length < 2) return null;
    const payload = parts[1].replace(/-/g, '+').replace(/_/g, '/');
    const pad = payload.length % 4 === 0 ? '' : '='.repeat(4 - (payload.length % 4));
    const json =
      typeof atob === 'function'
        ? atob(payload + pad)
        : Buffer.from(payload + pad, 'base64').toString('utf8');
    const parsed = JSON.parse(json) as { amr?: unknown };
    return parsed && typeof parsed === 'object' ? parsed : null;
  } catch {
    return null;
  }
}

/** Mensajes de GoTrue → copy corto para el form de nueva clave. */
export function userFacingPasswordUpdateMessage(raw: string): string {
  const m = (raw || '').trim();
  const lower = m.toLowerCase();
  if (!m) return 'No se pudo actualizar la contraseña.';
  if (lower.includes('session') || lower.includes('not authenticated') || lower.includes('auth session missing')) {
    return 'El enlace ya no vale. Pedí uno nuevo desde «¿Olvidaste tu contraseña?»';
  }
  if (lower.includes('same password') || lower.includes('should be different')) {
    return 'Elegí una contraseña distinta a la anterior.';
  }
  if (lower.includes('pwned') || lower.includes('known to be weak')) {
    return 'Esta contraseña aparece en filtraciones conocidas. Elegí otra distinta.';
  }
  if (lower.includes('weak') || (lower.includes('password') && lower.includes('least'))) {
    return 'La contraseña no cumple los requisitos. Usá al menos 8 caracteres.';
  }
  if (/[áéíóúñ¿¡]/i.test(m)) return m;
  return 'No se pudo guardar la contraseña. Pedí un enlace nuevo e intentá de nuevo.';
}
